import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, ViewChild} from '@angular/core';
import {WebcamImage, WebcamInitError, WebcamUtil} from 'ngx-webcam';
import {Observable, Subject} from 'rxjs';
import {DevicesService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {ApiService} from 'src/app/shared/services/api.service';
import {rotateBase64Image} from 'src/app/shared/utils/images';
import {environment} from '../../../environments/environment';
import {FaceDetector, FilesetResolver} from '@mediapipe/tasks-vision';
import {InferenceSession, Tensor, env} from 'onnxruntime-web';

interface remBgParams {
  mean: [number, number, number];
  std: [number, number, number];
  size: [number, number];
}
class remBg {
  private static modelFileName: string = environment.remBgModelFileName || 'u2net.onnx';
  private static mean: [number, number, number] = [0.485, 0.456, 0.406];
  private static std: [number, number, number] = [0.229, 0.224, 0.225];
  private static session: InferenceSession | undefined = undefined;
  private static model: ArrayBuffer | undefined = undefined;

  private static async initiateSession(): Promise<InferenceSession> {
    if(remBg.session !== undefined) {
      return remBg.session;
    }
    const staticPath = `${environment.backupUri || environment.baseUri}static/`;
    env.wasm.wasmPaths = staticPath;
    if(remBg.model === undefined) {
      const res = await fetch(`${staticPath}${this.modelFileName}`);
      if(!res.ok) {
        throw new Error(await res.text());
      }
      remBg.model = await res.arrayBuffer();
    }
    remBg.session = await InferenceSession.create(remBg.model);
    return remBg.session;
  }

  private static normalizeImage(canvas: HTMLCanvasElement,
    params: remBgParams,
    inputName: string = 'input.1'): {
    [key: string]: Tensor
  } {
    // Create a temporary canvas for resizing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = params.size[0];
    tempCanvas.height = params.size[1];
    const tempCtx = tempCanvas.getContext('2d');
    if(!tempCtx) {
      throw new Error('Failed to get context for temp canvas');
    }

    // Enable high-quality image smoothing (closest to LANCZOS)
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    // Resize image
    tempCtx.drawImage(canvas, 0, 0, params.size[0], params.size[1]);

    // Get pixel data
    const imageData = tempCtx.getImageData(0, 0, params.size[0], params.size[1]);
    const data = imageData.data;
    const width = params.size[0];
    const height = params.size[1];

    // Find max value across all pixels (like Python: im_ary / max(np.max(im_ary), 1e-6))
    let maxValue = 0;
    for(let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255.0;
      const g = data[i + 1] / 255.0;
      const b = data[i + 2] / 255.0;
      maxValue = Math.max(maxValue, r, g, b);
    }
    const divisor = Math.max(maxValue, 1e-6);

    // Create normalized array (CHW format: channels, height, width)
    const normalized = new Float32Array(3 * height * width);

    // Normalize each channel
    for(let y = 0; y < height; y++) {
      for(let x = 0; x < width; x++) {
        const pixelIndex = (y * width + x) * 4;
        const r = data[pixelIndex] / 255.0;
        const g = data[pixelIndex + 1] / 255.0;
        const b = data[pixelIndex + 2] / 255.0;

        // Apply dynamic normalization then (pixel - mean) / std
        // Matches Python: im_ary = im_ary / max(np.max(im_ary), 1e-6)
        const rNormalized = r / divisor;
        const gNormalized = g / divisor;
        const bNormalized = b / divisor;

        // Apply mean/std normalization
        const rNorm = (rNormalized - params.mean[0]) / params.std[0];
        const gNorm = (gNormalized - params.mean[1]) / params.std[1];
        const bNorm = (bNormalized - params.mean[2]) / params.std[2];

        // Store in CHW format
        normalized[y * width + x] = rNorm; // R channel
        normalized[height * width + y * width + x] = gNorm; // G channel
        normalized[2 * height * width + y * width + x] = bNorm; // B channel
      }
    }

    // Create ONNX tensor
    const tensor = new Tensor('float32', normalized, [1, 3, height, width]);

    return {[inputName]: tensor};
  }

  private static normalizeMask(mask: Float32Array,
    outputShape: [number, number, number, number]): Float32Array {
    const [, , height, width] = outputShape;
    // Extract the mask data
    // This shouldn't be necessary
    const maskData = mask.slice(0, height * width);
    if(mask.length !== height * width) {
      console.warn('[normalizeMask] Mask length does not match output shape', {
        maskLength: mask.length,
        outputShape: `${height}x${width}=${height * width}`
      });
    }

    // Find min/max for normalization
    let min = maskData[0];
    let max = maskData[0];
    for(let i = 1; i < maskData.length; i++) {
      if(maskData[i] < min) min = maskData[i];
      if(maskData[i] > max) max = maskData[i];
    }

    // Normalize to 0-1 range
    const normalized = new Float32Array(maskData.length);
    for(let i = 0; i < maskData.length; i++) {
      normalized[i] = (maskData[i] - min) / (max - min);
    }

    return normalized;
  }


  private static createGrayScaleMask(mask: Float32Array,
    {width, height}: { width: number; height: number }): HTMLCanvasElement {
    // Create canvas for mask
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskCtx = maskCanvas.getContext('2d');
    if(!maskCtx) {
      throw new Error('Failed to get context for mask canvas');
    }
    maskCtx.imageSmoothingEnabled = true;
    maskCtx.imageSmoothingQuality = 'high';
    const maskImageData = maskCtx.createImageData(width, height);

    // Set grayscale data
    for(let i = 0; i < mask.length; i++) {
      const value = Math.round(mask[i] * 255);
      const pixelIndex = i * 4;
      maskImageData.data[pixelIndex] = value; // R
      maskImageData.data[pixelIndex + 1] = value; // G
      maskImageData.data[pixelIndex + 2] = value; // B
      maskImageData.data[pixelIndex + 3] = 255; // A
    }

    maskCtx.putImageData(maskImageData, 0, 0);
    return maskCanvas;
  }

  private static resizeMask(maskCanvas: HTMLCanvasElement,
    originalSize: { width: number; height: number }): HTMLCanvasElement {
    // Resize to original image size
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = originalSize.width;
    resizedCanvas.height = originalSize.height;
    const resizedCtx = resizedCanvas.getContext('2d');
    if(!resizedCtx) {
      throw new Error('Failed to get context for resized canvas');
    }
    resizedCtx.imageSmoothingEnabled = true;
    resizedCtx.imageSmoothingQuality = 'high';
    resizedCtx.drawImage(maskCanvas,
      0,
      0,
      originalSize.width,
      originalSize.height);
    return resizedCanvas;
  }

  private static processModelOutput(output: Float32Array,
    originalSize: {
      width: number;
      height: number;
    },
    outputShape: [number, number, number, number]): HTMLCanvasElement {
    const normalized = remBg.normalizeMask(output, outputShape);

    // Extract model output dimensions from outputShape
    const [, , modelHeight, modelWidth] = outputShape;

    // Create mask canvas with mo%el output dimensions
    const grayscaleMaskCanvas = remBg.createGrayScaleMask(normalized, {
      width: modelWidth,
      height: modelHeight
    });

    // Resize to original image dimensions
    const resizedMaskCanvas = remBg.resizeMask(grayscaleMaskCanvas, originalSize);

    return resizedMaskCanvas;
  }

  static async dispose(): Promise<void> {
    if(remBg.session !== undefined) {
      await remBg.session.release();
      remBg.session = undefined;
    }
    remBg.model = undefined;
  }

  static async removeBackground(inputCanvas: HTMLCanvasElement): Promise<[HTMLCanvasElement, CanvasRenderingContext2D]> {
    const originalSize = {
      width: inputCanvas.width,
      height: inputCanvas.height
    };
    const session = await remBg.initiateSession();
    const params: remBgParams = {
      mean: remBg.mean,
      std: remBg.std,
      size: (session.inputMetadata[0] as InferenceSession.TensorValueMetadata).shape.slice(2) as [number, number]
    };
    const input = remBg.normalizeImage(inputCanvas, params);
    const mask = await session.run(input);
    const maskCanvas = remBg.processModelOutput(await Object.entries(mask)[0][1].getData() as Float32Array,
      originalSize,
      (session.outputMetadata[0] as InferenceSession.TensorValueMetadata).shape as [number, number, number, number]);
    const inputCtx = inputCanvas.getContext('2d');
    if(inputCtx === null) {
      throw new Error('Null inputCtx');
    }
    const maskCtx = maskCanvas.getContext('2d');
    if(maskCtx === null) {
      throw new Error('Null maskCtx');
    }
    const inputImageData = inputCtx.getImageData(0, 0, inputCanvas.width, inputCanvas.height);
    const maskImageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    for(let i = 0; i < inputImageData.data.length; i += 4) {
      // Use the red channel of the mask as alpha
      inputImageData.data[i + 3] = maskImageData.data[i];
    }
    inputCtx.putImageData(inputImageData, 0, 0);

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = inputCanvas.width;
    finalCanvas.height = inputCanvas.height;
    const finalCtx = finalCanvas.getContext('2d');
    if(finalCtx === null) {
      throw new Error('Null finalCtx');
    }
    // Fill with white
    finalCtx.fillStyle = '#fff';
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    // Draw the inputCanvas (with alpha) on top
    finalCtx.drawImage(inputCanvas, 0, 0);

    return [finalCanvas, finalCtx];
  }
}

@Component({
  selector: 'app-webcam',
  templateUrl: './webcam.component.html',
  styleUrls: ['./webcam.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WebcamComponent implements OnInit, OnDestroy {

  @ViewChild('webcam_element') webcamRef: any;

  @Input() backUrl: string;
  @Input() backUrlParams: any;
  @Input() enableBackUrl = true;
  @Input() imageQuality = 1;
  @Input() brokenButton = false;
  @Input() fullHeight = false;
  @Input() canRotateCamera = false;
  @Input() showOfflineWarning = false;
  @Input() documentCapture = false;
  @Input() includeFaceDetection = false;
  @Input() removeBackground = true;
  @Output() output: EventEmitter<any> = new EventEmitter<any>();
  @Output() emitManualProblem: EventEmitter<any> = new EventEmitter<any>();
  @Output() cameraProblem: EventEmitter<any> = new EventEmitter<any>();
  @Output() back: EventEmitter<any> = new EventEmitter<any>();

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }

  // toggle webcam on/off
  public showWebcam = false;
  public webcameIsInit = false;
  public multipleWebcamsAvailable = true;
  public mediaDevices: MediaDeviceInfo[];
  public deviceId: string;
  public videoOptions: MediaTrackConstraints = {
    width: {ideal: 1280}, // Need to specify otherwise some camera can fail to open because of unsupported browser auto selected resolution
    focusMode: 'manual',
    focusDistance: {ideal: 100.0}
  } as any;
  public errors: WebcamInitError[] = [];

  public webcamImage: WebcamImage = undefined;
  public videoInitialized: boolean;
  public deviceReady = false;
  public ratioDetected = false;

  private trigger: Subject<void> = new Subject<void>();
  private nextWebcam: Subject<boolean | string> = new Subject<boolean | string>();

  private resumeDeviceId: string;
  private nativeWidth = 0;
  private nativeHeight = 0;

  get effectiveAspectRatio(): string | null {
    if(!this.nativeWidth || !this.nativeHeight) return null;
    const rotate = localStorage.getItem('cameraRotate');
    const degrees = rotate ? parseInt(rotate) : 0;
    if(degrees === 90 || degrees === 270) {
      return `${this.nativeHeight} / ${this.nativeWidth}`;
    }
    return `${this.nativeWidth} / ${this.nativeHeight}`;
  }

  // Face detection properties
  private detector: FaceDetector | null = null;
  private running = false;
  private rafId: number | null = null;
  private stableFrames = 0;
  progressPercent = 0;
  canCapture = false;
  minDetectionConfidence = 0.6;
  stableFramesNeeded = 72;
  captureThreshold = 0.8;

  eventPause = this.onCameraPause.bind(this);
  eventResume = this.onCameraResume.bind(this);
  rotationClass: string = 'rotate-0';

  private static imageDetector: FaceDetector | undefined = undefined;

  constructor(public nav: NavigateService,
    public loader: LoaderService,
    private devices: DevicesService,
    public api: ApiService) {
  }

  public ngOnInit(): void {
    this.loader.loading(true);
    WebcamUtil.getAvailableVideoInputs().then((mediaDevices: MediaDeviceInfo[]) => {
      this.mediaDevices = mediaDevices;
      this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
      this.applyRotation();

      this.tryRestoreSavedDeviceId(mediaDevices); // Determine which device to use before showing the webcam

      this.showWebcam = true;

      if(this.documentCapture) {
        this.canCapture = true;
      } else if(environment.usesFaceDetectionForCapture && !this.documentCapture) {
        this.initDetector();
      }
    });
    if(this.devices.isDevices('cordova')) {
      document.addEventListener('pause', this.eventPause);
      document.addEventListener('resume', this.eventResume);
    }
  }

  public ngOnDestroy() {
    if(this.devices.isDevices('cordova')) {
      document.removeEventListener('pause', this.eventPause);
      document.removeEventListener('resume', this.eventResume);
    }
    this.stopFaceDetection();
    remBg.dispose().catch(err => console.warn('Error disposing remBg resources:', err));
  }

  private async initDetector(): Promise<void> {
    try {
      const staticBase = `${environment.backupUri || environment.baseUri}static`;
      const visionUrl = `${staticBase}`;
      const modelUrl = `${staticBase}/blaze_face_short_range.tflite`;

      const vision = await FilesetResolver.forVisionTasks(visionUrl);
      this.detector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: modelUrl
        },
        runningMode: 'VIDEO',
        minDetectionConfidence: this.minDetectionConfidence
      });

      // Start detection if camera is already ready
      if(this.deviceReady) {
        this.startFaceDetection();
      }
    } catch (err) {
      console.error('Failed to initialize face detector', err);
    }
  }

  private stopFaceDetection(): void {
    this.running = false;
    this.stableFrames = 0;
    this.progressPercent = 0;
    this.canCapture = false;
    if(this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private tryRestoreSavedDeviceId(mediaDevices?: MediaDeviceInfo[]): void {
    const devices = mediaDevices || this.mediaDevices;
    const savedDeviceId = localStorage.getItem('cameraDeviceId');
    if(savedDeviceId && devices.some(d => d.deviceId === savedDeviceId)) {
      this.deviceId = savedDeviceId;
    }
  }

  private startFaceDetection(): void {
    this.running = true;
    this.loop();
  }

  private loop(): void {
    if(!this.running) {
      if(!this.detector) this.canCapture = true;
      return;
    }

    const video = document.querySelector('#webcam_element video') as HTMLVideoElement;
    if(!video) {
      this.rafId = requestAnimationFrame(() => this.loop());
      return;
    }

    // Check if video is ready
    if(video.videoWidth === 0 || video.videoHeight === 0 || video.readyState < 2) {
      this.rafId = requestAnimationFrame(() => this.loop());
      return;
    }

    const now = performance.now();
    let result;
    try {
      result = this.detector.detectForVideo(video, now);
    } catch (err) {
      console.warn('Face detection failed:', err);
      this.rafId = requestAnimationFrame(() => this.loop());
      return;
    }
    const faces = result?.detections ?? [];

    if(faces.length > 0) {
      this.stableFrames++;
      const detection = faces[0];
      const score = detection.categories?.[0]?.score ?? 0;

      this.progressPercent = Math.min(100, (this.stableFrames / this.stableFramesNeeded) * 100);
      this.canCapture = this.stableFrames >= this.stableFramesNeeded && score >= this.captureThreshold;
    } else {
      this.stableFrames = 0;
      this.progressPercent = 0;
      this.canCapture = false;
    }

    this.rafId = requestAnimationFrame(() => this.loop());
  }

  public triggerSnapshot(): void {
    if(environment.usesFaceDetectionForCapture && !this.documentCapture && !this.canCapture) return;
    this.trigger.next();
  }

  public toggleWebcam(state: boolean): void {
    this.showWebcam = state || !this.showWebcam;
    if(!this.showWebcam) {
      this.turnOffCamera();
    }
  }

  public turnOffCamera(): void {
    this.deviceId = null;
    this.showWebcam = false;
    this.nextWebcam.complete();
    this.stopFaceDetection();
  }

  public handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
    this.loader.loading(false);
    this.cameraProblem.emit();
  }

  public showNextWebcam(directionOrDeviceId: boolean | string): void {
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    this.deviceReady = false;
    this.ratioDetected = false;
    this.loader.loading(true);
    // Clear cached dimensions for fresh ratio detection
    this.nativeWidth = 0;
    this.nativeHeight = 0;
    delete (this.videoOptions as any).aspectRatio;
    this.nextWebcam.next(directionOrDeviceId);
  }

  private async cropAroundHead(webcamImage: WebcamImage): Promise<WebcamImage> {
    // ICAO standard: aspect ratio 7:9, head centered, occupying 23/30 of the height, with a 1/10 margin above.
    if(WebcamComponent.imageDetector === undefined) {
      const staticBase = `${environment.backupUri || environment.baseUri}static`;
      const visionUrl = `${staticBase}`;
      const modelUrl = `${staticBase}/blaze_face_short_range.tflite`;

      const vision = await FilesetResolver.forVisionTasks(visionUrl);
      WebcamComponent.imageDetector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: modelUrl
        },
        runningMode: 'IMAGE',
        minDetectionConfidence: this.minDetectionConfidence
      });
    }
    const imageBitmap = await createImageBitmap(await (await fetch(webcamImage.imageAsDataUrl)).blob());
    const faces = WebcamComponent.imageDetector.detect(imageBitmap).detections;
    if(faces.length !== 1) {
      throw new Error('Could not detect a unique face');
    }
    const faceBox = faces[0].boundingBox;
    if(faceBox === undefined) {
      throw new Error('Undefined faceBox');
    }
    // Enlarge faceBox vertically to approximate chin-to-top-of-head
    const headTop = Math.max(0, faceBox.originY - 0.8 * faceBox.height);
    const headBottom = faceBox.originY + faceBox.height;
    const headHeight = headBottom - headTop;
    // Center horizontally, but allow for possible out-of-bounds
    const faceCenterX = faceBox.originX + faceBox.width / 2;
    // Now use this head box for cropping
    const cropHeight = headHeight * 30 / 23;
    const cropWidth = cropHeight * 7 / 9;
    const cropY = headTop - (cropHeight - headHeight) * 1 / 10; // margin above
    const cropX = faceCenterX - cropWidth / 2;

    // Clamp crop box to image boundaries
    const cropXClamped = Math.max(0, Math.min(imageBitmap.width - cropWidth, cropX));
    const cropYClamped = Math.max(0, Math.min(imageBitmap.height - cropHeight, cropY));
    const cropWidthClamped = Math.min(cropWidth, imageBitmap.width - cropXClamped);
    const cropHeightClamped = Math.min(cropHeight, imageBitmap.height - cropYClamped);

    // Crop original image
    const croppingCanvas = document.createElement('canvas');
    croppingCanvas.width = Math.round(cropWidthClamped);
    croppingCanvas.height = Math.round(cropHeightClamped);
    const croppingCtx = croppingCanvas.getContext('2d');
    if(croppingCtx === null) {
      throw new Error('Null croppingCtx');
    }
    croppingCtx.drawImage(imageBitmap,
      cropXClamped, cropYClamped, cropWidthClamped, cropHeightClamped,
      0, 0, cropWidthClamped, cropHeightClamped);
    if(!this.removeBackground) {
      return new WebcamImage(croppingCanvas.toDataURL(),
        (webcamImage as any)._mimeType,
        croppingCtx.getImageData(0, 0, Math.round(cropWidthClamped), Math.round(cropHeightClamped)));
    }
    const [finalCanvas, finalCtx] = await remBg.removeBackground(croppingCanvas);
    return new WebcamImage(finalCanvas.toDataURL(),
      (webcamImage as any)._mimeType,
      finalCtx.getImageData(0, 0, Math.round(cropWidthClamped), Math.round(cropHeightClamped)));
  }

  public async handleImage(webcamImage: WebcamImage): Promise<void> {
    const actualRotate = localStorage.getItem('cameraRotate');
    const degrees = actualRotate ? parseInt(actualRotate) : 0;
    this.loader.loading(true);
    this.showWebcam = false;
    this.stopFaceDetection();
    try {
      if(this.includeFaceDetection) {
        this.webcamImage = await this.cropAroundHead(webcamImage);
      } else {
        this.webcamImage = webcamImage;
      }
      if(degrees !== 0) {
        rotateBase64Image(this.webcamImage.imageAsDataUrl, degrees).then(rotatedImageBase64 => {
          this.output.emit(rotatedImageBase64);
        }).catch(() => {
          this.output.emit(this.webcamImage.imageAsDataUrl);
        });
      } else {
        this.output.emit(this.webcamImage.imageAsDataUrl);
      }
    } catch (err) {
      console.error('Failed to process captured image', err);
      this.showWebcam = true;
      this.loader.loading(true, {type: 'error', message: err instanceof Error ? err.message : 'camera.capture.error'});
      return;
    }
    this.loader.loading(false);
  }

  public cameraWasSwitched(deviceId: string): void {
    this.tryRestoreSavedDeviceId();
    // Initial trigger fired with default camera but we have a different saved device — switch silently
    if(!this.webcameIsInit && this.deviceId && this.deviceId !== deviceId) {
      this.showNextWebcam(this.deviceId);
      return;
    }
    this.deviceId = deviceId;
    localStorage.setItem('cameraDeviceId', deviceId);
    setTimeout(() => {
      this.webcameIsInit = true;
      this.deviceReady = true;
      this.updateNativeAspectRatio().then(() => {
        this.ratioDetected = true;
        this.loader.loading(false);
      });
      if(environment.usesFaceDetectionForCapture && !this.documentCapture && this.detector) {
        this.startFaceDetection();
      }
    }, 35);
  }

  private async updateNativeAspectRatio(): Promise<void> {
    const video = document.querySelector('#webcam_element video') as HTMLVideoElement;
    if(!video) return;

    const stream = video.srcObject as MediaStream | null;
    const tracks = stream?.getVideoTracks() ?? [];
    const track = tracks[0];
    if(!track) return;

    // getCapabilities() returns hardware limits, unaffected by constraints
    const caps = track.getCapabilities?.() as (MediaTrackCapabilities & { width?: ULongRange; height?: ULongRange }) | undefined;
    const maxW = caps?.width?.max;
    const maxH = caps?.height?.max;

    let nativeRatio: number;
    if(maxW && maxH) {
      nativeRatio = maxW / maxH;
    } else {
      // Fall back to negotiated settings
      const s = track.getSettings();
      const w = s.width ?? video.videoWidth;
      const h = s.height ?? video.videoHeight;
      if(!w || !h) return;
      nativeRatio = w / h;
    }

    // Apply aspect ratio constraint
    try {
      await track.applyConstraints({width: {ideal: 1280}, aspectRatio: {ideal: nativeRatio}});
    } catch {
      // Best-effort — some browsers / cameras ignore aspectRatio constraint
    }

    // Read final negotiated dimensions
    const finalSettings = track.getSettings();
    this.nativeWidth = finalSettings.width ?? video.videoWidth;
    this.nativeHeight = finalSettings.height ?? video.videoHeight;

    // Sync videoOptions for next camera switch
    (this.videoOptions).aspectRatio = {ideal: nativeRatio};
  }

  onCameraPause() {
    this.deviceReady = false;
    this.resumeDeviceId = this.deviceId;
    this.deviceId = null;
    this.nextWebcam.next(null);
    this.stopFaceDetection();
  }

  onCameraResume() {
    this.deviceId = this.resumeDeviceId;
    this.nextWebcam.next(this.deviceId);
    this.deviceReady = true;
    if(environment.usesFaceDetectionForCapture && !this.documentCapture && this.detector) {
      this.startFaceDetection();
    }
  }

  backUrlRedirect(backUrl: string) {
    this.back.emit();
    if(!this.enableBackUrl) return;
    if(backUrl === 'nfc-read-draw-signature') {
      this.nav.to(this.backUrl, undefined, {state: {type: 'manual'}, queryParams: this.backUrlParams});
    } else {
      this.nav.to(this.backUrl, undefined, {queryParams: this.backUrlParams});
    }
  }

  problemManualCamera() {
    this.emitManualProblem.emit();
  }

  rotateCamera() {
    const actualRotate = localStorage.getItem('cameraRotate');
    let newRotate;
    if(actualRotate !== null) {
      newRotate = (Number(actualRotate) + 90) % 360;
      if(newRotate === 0) {
        localStorage.removeItem('cameraRotate');
      } else {
        localStorage.setItem('cameraRotate', newRotate.toString());
      }
    } else {
      newRotate = 90;
      localStorage.setItem('cameraRotate', newRotate.toString());
    }
    this.applyRotation();
  }

  applyRotation() {
    const rotateValue = localStorage.getItem('cameraRotate');
    this.rotationClass = `rotate-${rotateValue ? rotateValue : 0}`;
  }

  get borderColor(): string {
    if(this.documentCapture || !environment.usesFaceDetectionForCapture) return 'transparent';
    const progress = this.progressPercent / 100;
    const hue = progress * 120; // red → green
    return `hsl(${hue}, 90%, 45%)`;
  }

  protected readonly ShortcutAction = ShortcutAction;
}